<script setup>
// Retomada individual: manda um template aprovado para o contato desta conversa.
//
// Diferente do botão nativo de templates do WhatsApp, que só existe quando a
// inbox da conversa é uma inbox de WhatsApp e usa os templates DELA: aqui a
// fonte é o agente de I.A. DA CONVERSA, resolvido pelo número da caixa de
// entrada. É o que faz a retomada existir em conversa de widget, de API e na
// inbox espelho do outbound, onde template nenhum havia.
//
// Havia um seletor de agente aqui. Ele pedia ao atendente uma informação que o
// sistema já tem e, pior, deixava escolher um agente que envia por outro
// número — o cliente receberia a mensagem de um telefone que não conhece. O
// seletor saiu; quando não há agente para a conversa, a tela diz isso.
//
// Quem assina o balão é escolha de quem envia: o agente de I.A. (pelo outbound)
// ou a própria pessoa (pelo canal da caixa de entrada).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import outboundsAPI from 'dashboard/api/outbounds';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import TemplateGallery from 'dashboard/routes/dashboard/settings/outbounds/components/TemplateGallery.vue';
import Button from 'dashboard/components-next/ui/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: Number, required: true },
  contactName: { type: String, default: '' },
  inboxPhone: { type: String, default: '' },
  inboxId: { type: [Number, String], default: null },
  // Quem NAO pode disparar ainda abre esta caixa: ela e o unico botao de
  // template do editor agora. Sem disparo ela vira a lista da propria caixa de
  // entrada, com envio pelo canal -- que e exatamente o que o botao antigo de
  // templates do WhatsApp fazia.
  podeDisparar: { type: Boolean, default: false },
});

const emit = defineEmits(['update:show', 'sent']);

const { t, locale } = useI18n();
const store = useStore();
const accountId = store.getters.getCurrentAccountId;

const digits = value => String(value || '').replace(/\D/g, '');

const chatbots = ref([]);
const selectedChatbotId = ref('');
const templates = ref([]);
const selectedTemplateName = ref('');
const loading = ref({ chatbots: false, templates: false, sending: false });

// Sempre as duas opcoes: a mensagem pode sair como o agente de I.A. da conversa
// ou como a pessoa que esta atendendo. Muda quem assina o balao na conversa,
// nao o template.
const modo = ref('agente');

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

// Meta só entrega template aprovado; listar o resto só convida um envio que falha.
// O AGENTE NAO E ESCOLHIDO: ele ja existe na conversa.
//
// Perguntar "selecione um agente" era pedir ao atendente uma informacao que o
// sistema ja tem -- e pior, deixava escolher um agente que envia por OUTRO
// numero, o que faz o cliente receber a mensagem de um telefone que ele nao
// conhece. O agente da conversa e aquele cujo numero e o numero da caixa de
// entrada dela.
const agenteDaConversa = computed(() =>
  chatbots.value.find(bot => {
    const doAgente = digits(bot.chatbot_phone);
    return doAgente && doAgente === digits(props.inboxPhone);
  })
);

// "Nenhum agente para esta conversa" so e verdade DEPOIS de a lista chegar.
// Antes disso a tela diz que esta carregando, e nao que nao ha agente.
const semAgenteDaConversa = computed(
  () => !loading.value.chatbots && !agenteDaConversa.value
);

// Os templates da propria caixa de entrada: a fonte de quem nao tem agente de
// disparo. Sao os mesmos que o botao antigo de templates do WhatsApp listava.
// A lista da caixa guarda UM REGISTRO POR NOME+IDIOMA, e a galeria identifica
// template pelo nome. Sem desempate, dois cards com o mesmo nome viram a mesma
// selecao e o cliente pode receber o idioma errado -- a lista do agente nao tem
// esse problema porque o Rails ja deduplica por nome antes de entregar.
//
// Desempate: o idioma da interface primeiro (pt_BR -> pt_BR, depois pt), e so
// entao o primeiro que aparecer. O idioma escolhido continua visivel no parser
// antes de enviar.
const templatesDaCaixa = computed(() => {
  const todos =
    store.getters['inboxes/getFilteredWhatsAppTemplates'](props.inboxId) || [];
  const daInterface = String(locale.value || '').toLowerCase();
  const base = daInterface.split(/[-_]/)[0];
  const nota = tpl => {
    const idioma = String(tpl.language || '').toLowerCase();
    if (idioma === daInterface) return 0;
    if (idioma.split(/[-_]/)[0] === base) return 1;
    return 2;
  };
  const porNome = new Map();
  todos.forEach(tpl => {
    const atual = porNome.get(tpl.name);
    if (!atual || nota(tpl) < nota(atual)) porNome.set(tpl.name, tpl);
  });
  return [...porNome.values()];
});

const templatesVisiveis = computed(() =>
  agenteDaConversa.value ? templates.value : templatesDaCaixa.value
);

const templateEscolhido = computed(() =>
  templatesVisiveis.value.find(tpl => tpl.name === selectedTemplateName.value)
);

// So o agente resolvido habilita o envio como agente. Sem ele sobra o envio
// como usuario, que nao depende de outbound nenhum.
const podeEnviarComoAgente = computed(() => Boolean(agenteDaConversa.value));

const sendableTemplates = computed(() =>
  templatesVisiveis.value.filter(
    // Alguns provedores guardam 'approved' minusculo; comparar cru escondia a
    // lista inteira e deixava a galeria vazia com o botao visivel.
    template =>
      !template.status || String(template.status).toUpperCase() === 'APPROVED'
  )
);

const canSend = computed(() =>
  Boolean(selectedChatbotId.value && selectedTemplateName.value)
);

// REPORTS-03 (R3-17, revisao R-B): a recusa de quem pediu para parar e montada
// aqui, no idioma da tela. O 422 traz `pediu_em`, `origem` e o fuso do
// Outbound, e o dia sai nesse fuso, o mesmo do texto do servidor; o `error`
// dele, em portugues, fica para quem chama a API. Os outros erros do Outbound
// chegam prontos no `error`.
const recusaDePediuParaParar = data => {
  if (!data?.origem || !data?.pediu_em) return null;
  // As origens de `ContactOptOut::ORIGENS`.
  const origens = {
    meta_131050: t('OUTBOUNDS.RETOMADA.OPTED_OUT_ORIGIN.META_131050'),
    botao: t('OUTBOUNDS.RETOMADA.OPTED_OUT_ORIGIN.BOTAO'),
    texto: t('OUTBOUNDS.RETOMADA.OPTED_OUT_ORIGIN.TEXTO'),
    manual: t('OUTBOUNDS.RETOMADA.OPTED_OUT_ORIGIN.MANUAL'),
  };
  const dia = new Intl.DateTimeFormat(String(locale.value).replace('_', '-'), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: data.timezone,
  }).format(new Date(data.pediu_em));
  return t('OUTBOUNDS.RETOMADA.OPTED_OUT', {
    date: dia,
    origin: origens[data.origem],
  });
};

const errorMessage = (error, fallback) =>
  recusaDePediuParaParar(error?.response?.data) ||
  error?.response?.data?.error ||
  fallback;

async function fetchChatbots() {
  loading.value.chatbots = true;
  try {
    chatbots.value = await outboundsAPI.chatbots(accountId);
  } catch (error) {
    useAlert(errorMessage(error, t('OUTBOUNDS.RETOMADA.AGENTS_ERROR')));
  } finally {
    loading.value.chatbots = false;
  }
}

watch(selectedChatbotId, async id => {
  templates.value = [];
  selectedTemplateName.value = '';
  if (!id) return;

  loading.value.templates = true;
  try {
    templates.value = await outboundsAPI.templates(accountId, id);
  } catch (error) {
    useAlert(errorMessage(error, t('OUTBOUNDS.RETOMADA.TEMPLATES_ERROR')));
  } finally {
    loading.value.templates = false;
  }
});

// A lista de agentes só é buscada quando a caixa abre: ela é uma chamada por
// conversa aberta, e quem nunca clica no botão não deve pagar por ela.
watch(
  () => props.show,
  async isOpen => {
    if (!isOpen) return;
    // Estado de conversa anterior nao pode sobrar: o nome de quem atende e a
    // lista de templates sao DAQUELA conversa. Sem isto, abrir uma conversa sem
    // agente logo depois de uma com agente mostraria o nome do agente errado na
    // mensagem de ausencia.
    modo.value = 'agente';
    selectedTemplateName.value = '';
    // Chamar /outbounds/chatbots sem poder disparar seria pedir um 403: a rota
    // exige a feature ligada e papel de administrador. Quem nao pode disparar
    // usa esta caixa como a lista de templates da propria caixa de entrada.
    if (props.podeDisparar && !chatbots.value.length) await fetchChatbots();
    // Seleciona sozinho: e o watcher de selectedChatbotId que busca os
    // templates, entao atribuir aqui ja traz a lista do agente da conversa.
    selectedChatbotId.value = agenteDaConversa.value?.id || '';
    // Sem agente so existe um caminho, e a caixa ja abre nele.
    if (!podeEnviarComoAgente.value) modo.value = 'usuario';
  }
);

// Como USUARIO o envio vai pelo canal da propria caixa de entrada, com o mesmo
// payload que o botao nativo de templates do WhatsApp monta -- inclusive as
// variaveis, que o WhatsAppTemplateParser preenche. Como AGENTE o envio vai
// pelo outbound, que preenche as variaveis a partir do contato.
async function enviarComoUsuario(payload) {
  loading.value.sending = true;
  try {
    // O template veio do agente, e a normalizacao do outbound nao carrega o
    // namespace -- campo que o envio pelo canal da caixa de entrada pode exigir,
    // dependendo de como a WABA foi provisionada. Quando a caixa tem o MESMO
    // template na lista dela, o namespace sai de la. Quando nao tem, segue sem:
    // e o que o proprio botao nativo faz nas contas que nao usam namespace.
    const doInbox =
      store.getters['inboxes/getWhatsAppTemplates'](props.inboxId) || [];
    const mesmo = doInbox.find(
      tpl =>
        tpl.name === payload.templateParams?.name &&
        (!payload.templateParams?.language ||
          tpl.language === payload.templateParams.language)
    );
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.conversationId,
      ...payload,
      templateParams: {
        ...payload.templateParams,
        namespace: payload.templateParams?.namespace || mesmo?.namespace,
      },
    });
    // O caminho antigo passava pelo ReplyBox.sendMessage, que avisava a
    // conversa. Sem estes dois eventos o envio fica MUDO: a lista nao rola ate
    // a mensagem nova e quem esta olhando acha que nao saiu.
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    emitter.emit(BUS_EVENTS.MESSAGE_SENT);
    selectedTemplateName.value = '';
    localShow.value = false;
    emit('sent');
  } catch (error) {
    useAlert(errorMessage(error, t('OUTBOUNDS.RETOMADA.SEND_ERROR')));
  } finally {
    loading.value.sending = false;
  }
}

async function send() {
  loading.value.sending = true;
  try {
    await outboundsAPI.sendTemplateToConversation(
      accountId,
      props.conversationId,
      {
        chatbotId: selectedChatbotId.value,
        templateName: selectedTemplateName.value,
      }
    );
    // O envio é assíncrono no backend Go: a mensagem aparece na conversa
    // quando a materialização a alcança, alguns segundos depois. Dizer
    // "enviado" agora seria mentir sobre a entrega, então o texto fala em fila.
    useAlert(t('OUTBOUNDS.RETOMADA.QUEUED'));
    selectedTemplateName.value = '';
    localShow.value = false;
    emit('sent');
  } catch (error) {
    useAlert(errorMessage(error, t('OUTBOUNDS.RETOMADA.SEND_ERROR')));
  } finally {
    loading.value.sending = false;
  }
}
</script>

<template>
  <woot-modal v-model:show="localShow" size="modal-big">
    <!-- Antes de escolher o agente o corpo do modal tem ~220px, e a lista do
         ComboBox tem 240px (max-h-60) posicionada em absolute: o
         `.modal-container` é overflow-auto e cortava a lista de agentes em
         ~40px, deixando só o primeiro nome à mostra. A altura mínima existe
         para a lista caber; depois que o agente é escolhido a galeria já dá
         essa altura, e ela sai do caminho. -->
    <div
      class="flex flex-col gap-4 p-8"
      :class="{ 'min-h-[28rem]': !selectedChatbotId }"
    >
      <header>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('OUTBOUNDS.RETOMADA.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ t('OUTBOUNDS.RETOMADA.SUBTITLE', { name: contactName }) }}
        </p>
      </header>

      <!-- O agente da conversa, não um seletor: ele já existe, e trocá-lo
           mandaria a mensagem de um número que o cliente não conhece. -->
      <div
        v-if="podeDisparar && loading.chatbots"
        class="px-4 py-3 text-sm border rounded-xl border-n-weak text-n-slate-10"
      >
        {{ t('OUTBOUNDS.RETOMADA.AGENT_LOADING') }}
      </div>

      <div
        v-else-if="podeDisparar && semAgenteDaConversa"
        class="flex items-start gap-2 px-4 py-3 border rounded-xl border-n-amber-6 bg-n-amber-2"
      >
        <i
          class="i-lucide-alert-triangle size-4 mt-0.5 shrink-0 text-n-amber-11"
        />
        <p class="text-xs text-n-amber-11">
          {{ t('OUTBOUNDS.RETOMADA.NO_AGENT', { inbox: inboxPhone }) }}
        </p>
      </div>

      <div
        v-else-if="podeEnviarComoAgente"
        class="flex items-center gap-2 px-4 py-3 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <i class="i-lucide-bot size-4 shrink-0 text-n-slate-11" />
        <p class="text-sm text-n-slate-12">
          {{
            t('OUTBOUNDS.RETOMADA.AGENT_OF_CONVERSATION', {
              name: agenteDaConversa?.name || agenteDaConversa?.chatbot_phone,
              phone: agenteDaConversa?.chatbot_phone,
            })
          }}
        </p>
      </div>

      <TemplateGallery
        v-model="selectedTemplateName"
        :templates="sendableTemplates"
        :loading="loading.templates"
      />

      <p
        v-if="!podeEnviarComoAgente && selectedTemplateName"
        class="text-xs text-n-slate-10"
      >
        {{ t('OUTBOUNDS.RETOMADA.ONLY_AS_USER') }}
      </p>

      <!-- As duas opções, quando as duas existem: muda quem assina o balão. -->
      <div
        v-if="podeEnviarComoAgente && selectedTemplateName"
        class="flex flex-col gap-2"
      >
        <span class="text-xs font-medium uppercase text-n-slate-10">
          {{ t('OUTBOUNDS.RETOMADA.SEND_AS') }}
        </span>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 px-3 py-2 text-sm border rounded-lg transition-colors"
            :class="
              modo === 'agente'
                ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="modo = 'agente'"
          >
            {{
              t('OUTBOUNDS.RETOMADA.AS_AGENT', {
                name: agenteDaConversa?.name || agenteDaConversa?.chatbot_phone,
              })
            }}
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-2 text-sm border rounded-lg transition-colors"
            :class="
              modo === 'usuario'
                ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="modo = 'usuario'"
          >
            {{ t('OUTBOUNDS.RETOMADA.AS_USER') }}
          </button>
        </div>
      </div>

      <!-- Como usuário o envio passa pelo canal da caixa de entrada, e as
           variáveis do template são preenchidas aqui — é o mesmo componente do
           botão nativo de templates. -->
      <WhatsAppTemplateParser
        v-if="modo === 'usuario' && templateEscolhido"
        :template="templateEscolhido"
        @send-message="enviarComoUsuario"
        @reset-template="selectedTemplateName = ''"
      >
        <!-- O parser NAO desenha botao: ele expoe um slot e quem o usa e que
             poe as acoes. Sem preencher este slot, escolher um template levava
             a uma tela sem nenhum jeito de enviar. -->
        <template #actions="{ sendMessage, resetTemplate, disabled }">
          <footer class="flex gap-2 justify-end">
            <Button variant="outline" @click="resetTemplate">
              {{ t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
            </Button>
            <Button
              :disabled="disabled || loading.sending"
              @click="sendMessage"
            >
              {{
                loading.sending
                  ? t('OUTBOUNDS.RETOMADA.SENDING')
                  : t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL')
              }}
            </Button>
          </footer>
        </template>
      </WhatsAppTemplateParser>

      <footer class="flex justify-end gap-2">
        <Button variant="outline" @click="localShow = false">
          {{ t('OUTBOUNDS.CANCEL') }}
        </Button>
        <Button
          v-if="modo === 'agente'"
          :disabled="!canSend || loading.sending"
          @click="send"
        >
          {{
            loading.sending
              ? t('OUTBOUNDS.RETOMADA.SENDING')
              : t('OUTBOUNDS.RETOMADA.SEND')
          }}
        </Button>
      </footer>
    </div>
  </woot-modal>
</template>
